import getDomain from '@/utils/domain.js';
import { getPage, getRouters, ROUTES } from '@router/routers';
import adminRequest from '@services/admin';
import { showToast } from '@utils/toastr';

const router = (() => {
    // State variables
    let currentLayout = null;
    let currentLayoutName = null;
    let currentLayoutModule = null;
    let currentPage = null;
    let currentModulePage = null;
    let currentModulePageName = null;

    // Theme helpers
    const getRouteConfig = (layoutType, page) => {
        if (!layoutType || !page) {
            return null;
        }
        return getPage(page, layoutType);
    };

    const resolveThemeFromRoute = routeConfig => {
        if (!routeConfig) {
            return 'default';
        }
        if (routeConfig.theme) {
            return routeConfig.theme;
        }

        const modulePath = routeConfig.module || '';
        if (modulePath.startsWith('admin/')) {
            return 'admin';
        }
        if (modulePath.startsWith('staff/')) {
            return 'staff';
        }
        if (modulePath.startsWith('user/')) {
            return 'user';
        }
        return 'default';
    };

    const applyThemeFor = (layoutType, page) => {
        const route = getRouteConfig(layoutType, page);
        const themeName = resolveThemeFromRoute(route);
        document.documentElement.setAttribute('data-theme', themeName);
    };

    // Get layout type from URL
    const getLayoutTypeFromURL = () => {
        const pathname = window.location.pathname;
        if (pathname.includes('auth.php') || pathname.includes('/user/auth.php') || pathname.includes('/admin/auth.php') || pathname.includes('/staff/auth.php')) {
            return 'auth';
        } else if (pathname.includes('a_frame.php') || pathname.includes('u_frame.php') || pathname.includes('s_frame.php')) {
            return 'main';
        }
        // Default to auth in dev when serving from / or /index.html
        if (pathname === '/' || pathname.endsWith('/index.html')) {
            return 'auth';
        }
        return null;
    };

    // Get page from URL
    const getPageFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const page = urlParams.get('page');

        // Adjust page based on current path for auth routes
        // const currentPath = window.location.pathname;
        // if (currentPath.includes('/user/') || currentPath.includes('user/u_auth.php')) {
        //     // If accessing user auth but page is admin route, default to user route
        //     if (page && page.startsWith('a_login')) {
        //         page = page.replace('a_login', 'u_login');
        //     } else if (!page || !page.startsWith('u_login')) {
        //         page = 'u_login_step_one';
        //     }
        // } else if (currentPath.includes('/admin/') || currentPath.includes('admin/auth.php')) {
        //     // If accessing admin auth but page is user route, default to admin route
        //     if (page && page.startsWith('u_login')) {
        //         page = page.replace('u_login', 'a_login');
        //     } else if (!page || !page.startsWith('a_login')) {
        //         page = 'a_login_step_one';
        //     }
        // }

        return page;
    };

    // Get params from URL
    const getParamsFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = {};

        for (const [key, value] of urlParams) {
            if (key !== 'page') {
                params[key] = value;
            }
        }

        return params;
    };

    // Create URL from page and params
    const createURLFromPageAndParams = (page, params = {}) => {
        const urlParams = new URLSearchParams();
        urlParams.set('page', page);

        // Add params to URL
        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                urlParams.set(key, params[key]);
            }
        });

        return `${window.location.pathname}?${urlParams.toString()}`;
    };

    // Load controller
    const loadLayoutModule = async filename => {
        const domain = getDomain();
        // eslint-disable-next-line no-console
        delete window.admin;
        delete window.user;
        delete window.staff;

        const route = ROUTES[filename];

        if (!route) {
            return false;
        }

        try {
            // Import layout controllers via static paths so webpack can bundle them
            let module;
            if (filename === 'auth.php') {
                if (domain === 'user') {
                    module = await import('../layouts/user/auth/index.js');
                } else if (domain === 'staff') {
                    module = await import('../layouts/staff/auth/index.js');
                } else {
                    module = await import('../layouts/admin/auth/index.js');
                }
            } else if (domain === 'admin') {
                module = await import('../layouts/admin/index.js');
            } else if (domain === 'user') {
                module = await import('../layouts/user/index.js');
            } else if (domain === 'staff') {
                module = await import('../layouts/staff/index.js');
            } else {
                return false;
            }

            if (filename === 'auth.php') {
                const authModule = module.default();

                if (domain === 'admin') {
                    window.admin = {
                        mainLayout: authModule
                    };
                } else if (domain === 'staff') {
                    window.staff = {
                        mainLayout: authModule
                    };
                } else {
                    window.user = {
                        mainLayout: authModule
                    };
                }

                currentLayout = 'auth';
                currentLayoutName = filename;
                currentLayoutModule = authModule;

                return authModule;
            }

            if (domain === 'admin' && filename === 'a_frame.php') {
                const mainModule = module.default();
                window.admin = {
                    mainLayout: mainModule
                };

                currentLayout = 'main';
                currentLayoutName = filename;
                currentLayoutModule = mainModule;

                return mainModule;
            }

            if (domain === 'user' && filename === 'u_frame.php') {
                const userMainModule = module.default();
                window.user = {
                    mainLayout: userMainModule
                };

                currentLayout = 'main';
                currentLayoutName = filename;
                currentLayoutModule = userMainModule;

                return userMainModule;
            }
            if (domain === 'staff' && filename === 's_frame.php') {
                const staffMainModule = module.default();
                window.staff = {
                    mainLayout: staffMainModule
                };

                currentLayout = 'main';
                currentLayoutName = filename;
                currentLayoutModule = staffMainModule;

                return staffMainModule;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading controller:', error);
            return false;
        }
    };

    // Load content
    const loadPage = async (layoutType, page, params, history = true) => {
        try {
            let filename;
            if (layoutType === 'auth') {
                filename = 'auth.php';
            } else if (layoutType === 'main') {
                const domain = getDomain();
                filename = domain === 'user' ? 'u_frame.php' : domain === 'staff' ? 's_frame.php' : 'a_frame.php';
            }

            const module = await loadLayoutModule(filename);

            if (module) {
                // Apply theme before initializing layout content
                applyThemeFor(layoutType, page);
                await module.init(page, params, history);
            } else {
                // eslint-disable-next-line no-console
                console.error('Router: Module not found');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Router: Error navigating to layout', error);
        }
    };

    // Get validated params
    const DEFAULT_PAGE = {
        auth: {
            admin: 'a_login_step_one',
            user: 'u_login_step_one',
            staff: 's_login_step_one',
            default: 'a_login_step_one'
        },
        main: {
            admin: 'a_shipping_request',
            user: 'u_delivery_destination_list',
            staff: 's_request_frame',
            default: 'a_shipping_request'
        }
    };

    const pickDefaultPage = (layoutType, domain) => DEFAULT_PAGE[layoutType]?.[domain] || DEFAULT_PAGE[layoutType].default;

    const extractParams = (routeParams, inputParams) =>
        routeParams.reduce((acc, key) => {
            const value = inputParams[key];
            // Include all parameters, even empty strings, for URL preservation
            if (value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

    const getValidatedParams = (layoutType, page, params = {}) => {
        const domain = getDomain();
        const path = window.location.pathname || '';
        const isUserMain = domain === 'user' || path.includes('user/auth.php');

        const routes = getRouters();
        const fallback = layoutType === 'auth' ? pickDefaultPage('auth', domain) : isUserMain ? 'u_delivery_destination_list' : pickDefaultPage('main', domain);

        // Check if page exists and show toast immediately if not found
        if (!routes[page]) {
            showToast('ページが見つかりません。', 'error');
        }

        let validPage = routes[page] ? page : fallback;
        const route = routes[validPage];

        if (!Array.isArray(route?.params)) {
            return { validPage, validatedParams: {} };
        }

        const validatedParams = extractParams(route.params, params);
        // Check if all required parameters are present (more lenient validation)
        const hasAllRequiredParams = route.params.every(param => Object.prototype.hasOwnProperty.call(params, param) && params[param] !== null && params[param] !== undefined);

        if (!hasAllRequiredParams && page !== 'a_item_insert') {
            validPage = pickDefaultPage(layoutType, domain);
            return { validPage, validatedParams: {} };
        }

        return { validPage, validatedParams };
    };

    // Check if page is in login flow
    const isLoginFlowPage = page => {
        if (!page) return false;
        return page.includes('login_step_one') || page.includes('login_step_two');
    };

    // Handle popstate
    const handlePopState = async event => {
        // Handle both jQuery event and native event
        const nativeEvent = event.originalEvent || event;
        const stateData = nativeEvent.state;
        const domain = getDomain();

        if (stateData && typeof stateData === 'object') {
            const { layoutType, page, params, state } = stateData;

            if (!layoutType || !page) {
                // eslint-disable-next-line no-console
                console.error('Router: Invalid state structure');
                return;
            }

            // Check if navigating within login flow
            if (isLoginFlowPage(page)) {
                window.location.reload();
                // Always redirect to login_step_one when in login flow
                const loginStepOnePage = pickDefaultPage('auth', domain);
                if (page !== loginStepOnePage) {
                    // Replace current history state to prevent back button issues
                    const newUrl = createURLFromPageAndParams(loginStepOnePage, {});
                    window.history.replaceState(
                        {
                            layoutType: 'auth',
                            page: loginStepOnePage,
                            params: {},
                            state: {}
                        },
                        '',
                        newUrl
                    );
                    await loadPage('auth', loginStepOnePage, {}, false);
                    return;
                }
            }

            // Check if layout has changed
            if (layoutType !== currentLayout) {
                const requireAuth = ROUTES[layoutType === 'main' ? 'a_frame.php' : 'auth.php'].requiresAuth;

                const response = await adminRequest.checkLogin();

                if (response?.status === 'success') {
                    const { loggedIn } = response?.data ?? {};

                    if ((!requireAuth && loggedIn) || (requireAuth && !loggedIn)) {
                        window.history.forward();
                        return;
                    }
                }
            } else {
                // Same layout, just load content without updating history
                if (window[domain] && window[domain].mainLayout) {
                    // Update theme on in-layout navigation via history
                    applyThemeFor(layoutType, page);
                    await window[domain].mainLayout.loadContent(page, state, params, false);
                }
            }
        }
    };

    // Handle initial page load
    const handleInitialPage = async () => {
        let layoutType = getLayoutTypeFromURL();
        const page = getPageFromURL();
        const params = getParamsFromURL();

        // Default to auth login step when layout type cannot be inferred
        if (!layoutType) {
            layoutType = 'auth';
        }

        const { validPage, validatedParams } = getValidatedParams(layoutType, page, params);

        // Create new URL with valid page and params
        const newUrl = createURLFromPageAndParams(validPage, validatedParams);

        window.history.replaceState(
            {
                layoutType,
                page: validPage,
                params: validatedParams,
                state: {}
            },
            '',
            newUrl
        );

        // Apply theme on initial load
        applyThemeFor(layoutType, validPage);
        await loadPage(layoutType, validPage, validatedParams, false);
    };

    // Init
    const init = async () => {
        // Handle popstate (browser back/forward)
        // $(window).on('popstate', handlePopState);

        await handleInitialPage();
    };

    // Return public functions
    return {
        init,
        loadLayoutModule,
        loadPage,
        getLayoutTypeFromURL,
        setCurrentPage: page => {
            currentPage = page;
        },
        setCurrentModulePage: modulePage => {
            currentModulePage = modulePage;
        },
        setCurrentModulePageName: modulePageName => {
            currentModulePageName = modulePageName;
        },
        getCurrentPage: () => currentPage,
        getCurrentModulePage: () => currentModulePage,
        getCurrentModulePageName: () => currentModulePageName,
        currentLayoutName,
        currentLayoutModule
    };
})();

export default router;
